/**
 * Centralized Query Keys Factory
 * Provides type-safe, consistent query key generation across the app
 * 
 * Pattern: ["scope", "entityId", "resource", ...params]
 * 
 * Benefits:
 * - Prevents typos and inconsistent keys
 * - Enables targeted cache invalidation
 * - Type-safe query key prefixes for cache operations
 */

// ========== Organization-scoped Query Keys ==========
export const orgQueryKeys = {
  all: (orgId: string) => ['org', orgId] as const,
  
  // Form responses / submissions
  responses: (orgId: string) => ['org', orgId, 'responses'] as const,
  responsesList: (orgId: string, filters?: string, page?: number) => 
    ['org', orgId, 'responses', 'list', filters ?? 'default', page ?? 1] as const,
  responseDetail: (orgId: string, responseId: string) => 
    ['org', orgId, 'responses', 'detail', responseId] as const,
  
  // Legacy submissions (shares table)
  legacySubmissions: (orgId: string, filters?: string, page?: number) => 
    ['org', orgId, 'applications', 'legacy_shares', filters ?? 'default', page ?? 1] as const,
  
  // Form templates
  templates: (orgId: string) => ['org', orgId, 'templates'] as const,
  templateDetail: (orgId: string, templateId: string) => 
    ['org', orgId, 'templates', 'detail', templateId] as const,
  
  // Form invitations
  invitations: (orgId: string, userId?: string | null) => 
    userId 
      ? ['org', orgId, 'invitations', 'user', userId] as const
      : ['org', orgId, 'invitations'] as const,
  invitationDetail: (orgId: string, invitationId: string) => 
    ['org', orgId, 'invitations', 'detail', invitationId] as const,
  
  // Renewal requests
  renewalRequests: (orgId: string) => ['org', orgId, 'renewal-requests'] as const,
  renewalRequestsCount: (orgId: string) => ['org', orgId, 'renewal-requests', 'count'] as const,
  
  // Audit events
  audit: (orgId: string, page?: number) => 
    ['org', orgId, 'audit', page ?? 1] as const,
  
  // Team members
  team: (orgId: string) => ['org', orgId, 'team'] as const,
  teamMember: (orgId: string, memberId: string) => 
    ['org', orgId, 'team', 'member', memberId] as const,
  
  // Organization settings/profile
  settings: (orgId: string) => ['org', orgId, 'settings'] as const,
  
  // Analytics (org-level)
  analytics: (orgId: string, range?: string) => 
    ['org', orgId, 'analytics', range ?? 'default'] as const,
} as const;

// ========== User-scoped Query Keys ==========
export const userQueryKeys = {
  all: (userId: string) => ['user', userId] as const,
  
  // Profile data
  profile: (userId: string) => ['user', userId, 'profile'] as const,
  autofillProfile: (userId: string) => ['user', userId, 'autofill-profile'] as const,
  
  // Vault / documents
  vault: (userId: string) => ['user', userId, 'vault'] as const,
  vaultDocuments: (userId: string) => ['user', userId, 'vault-documents'] as const,
  
  // Saved/filled forms
  savedForms: (userId: string) => ['user', userId, 'saved-forms'] as const,
  savedFormDetail: (userId: string, formId: string) => 
    ['user', userId, 'saved-forms', 'detail', formId] as const,
  
  // Form invitations (user-received)
  formInvitations: (userId: string) => ['user', userId, 'form-invitations'] as const,
  formInvitationDetail: (userId: string, invitationId: string) => 
    ['user', userId, 'form-invitations', 'detail', invitationId] as const,
  
  // Family members
  familyMembers: (userId: string) => ['user', userId, 'family-members'] as const,
  familyMemberDetail: (userId: string, memberId: string) => 
    ['user', userId, 'family-members', 'detail', memberId] as const,
  
  // Notifications & preferences
  notifications: (userId: string) => ['user', userId, 'notifications'] as const,
  deliveryPreferences: (userId: string) => ['user', userId, 'delivery-preferences'] as const,
  
  // Expiry reminders
  expiryReminders: (userId: string) => ['user', userId, 'expiry-reminders'] as const,
  
  // Digital signatures
  signatures: (userId: string) => ['user', userId, 'signatures'] as const,
  
  // Billing / subscription
  subscription: (userId: string) => ['user', userId, 'subscription'] as const,
  
  // Audit trail (user-specific)
  auditTrail: (userId: string, entityId?: string) => 
    entityId 
      ? ['user', userId, 'audit-trail', entityId] as const
      : ['user', userId, 'audit-trail'] as const,
} as const;

// ========== Platform/Admin Query Keys ==========
export const adminQueryKeys = {
  all: () => ['admin'] as const,
  
  // Platform-wide analytics
  analytics: (range?: string) => ['admin', 'analytics', range ?? 'default'] as const,
  
  // All organizations
  organizations: () => ['admin', 'organizations'] as const,
  organizationDetail: (orgId: string) => ['admin', 'organizations', 'detail', orgId] as const,
  
  // Data subject requests
  dataRequests: (status?: string) => 
    status 
      ? ['admin', 'data-requests', 'status', status] as const
      : ['admin', 'data-requests'] as const,
  
  // System audit
  auditEvents: (page?: number) => ['admin', 'audit-events', page ?? 1] as const,
} as const;

// ========== Shared / Static Query Keys ==========
export const sharedQueryKeys = {
  // Public pricing data
  pricing: () => ['shared', 'pricing'] as const,
  
  // Static config
  config: () => ['shared', 'config'] as const,
} as const;

/**
 * Cache invalidation helpers
 * Use with queryClient.invalidateQueries({ queryKey: ... })
 */
export const invalidationKeys = {
  /** Invalidate all org data for a specific organization */
  orgAll: (orgId: string) => ({ queryKey: orgQueryKeys.all(orgId) }),
  
  /** Invalidate all user data for a specific user */
  userAll: (userId: string) => ({ queryKey: userQueryKeys.all(userId) }),
  
  /** Invalidate org responses/submissions */
  orgResponses: (orgId: string) => ({ queryKey: orgQueryKeys.responses(orgId) }),
  
  /** Invalidate user's saved forms */
  userSavedForms: (userId: string) => ({ queryKey: userQueryKeys.savedForms(userId) }),
} as const;
